function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

// utils
import { findIndex, findIndexAfterFirst, isFunction, splice, unshift } from './utils';

/**
 * @private
 *
 * @class Cache
 *
 * @classdesc
 * class that is similar to the Map infrastructure, but faster and
 * more targeted to moize use cases
 */


// types

var Cache = function () {
  function Cache(options) {
    _classCallCheck(this, Cache);

    this.expirations = [];
    this.lastItem = {};
    this.list = [];
    this.size = 0;

    this.options = options;
  }

  /**
   * @function add
   * @memberof Cache
   * @instance
   *
   * @description
   * add a new item to cache
   *
   * @param {*} key the key to assign
   * @param {*} value the value to assign at key
   */
  Cache.prototype.add = function add(key, value) {
    this.lastItem = unshift(this.list, {
      key: key,
      value: value
    });

    this.size++;
  };

  /**
   * @function clear
   * @memberof Cache
   * @instance
   *
   * @description
   * clear the cache of all items
   */


  Cache.prototype.clear = function clear() {
    this.lastItem = {};
    this.list.length = this.size = 0;
  };

  /**
   * @function expireAfter
   * @memberof Cache
   * @instance
   *
   * @description
   * remove from cache after maxAge time has passed
   *
   * @param {*} key the key to remove
   */


  Cache.prototype.expireAfter = function expireAfter(key) {
    var _this = this;

    var _options = this.options,
        maxAge = _options.maxAge,
        onExpire = _options.onExpire;


    var timeoutId = setTimeout(function () {
      var index = findIndex(_this.list, key);

      if (~index) {
        var value = _this.list[index];

        _this.remove(key);
        _this.expirations.splice(findIndex(_this.expirations, key), 1);

        // $FlowIgnore onExpire will only fire if it is a function
        if (isFunction(onExpire) && onExpire(key.key) === false) {
          _this.add(key, value);

          return _this.expireAfter(key);
        }
      }
    }, maxAge);

    var indexOfKey = findIndex(this.expirations, key);

    if (~indexOfKey) {
      clearTimeout(this.expirations[indexOfKey].timeoutId);
      this.expirations[indexOfKey].timeoutId = timeoutId;
    } else {
      this.expirations.push({ key: key, timeoutId: timeoutId });
    }
  };

  /**
   * @function get
   * @memberof Cache
   * @instance
   *
   * @description
   * get the value of an item from cache if it exists
   *
   * @param {*} key the key to get the value of
   * @returns {*} the value at key
   */


  Cache.prototype.get = function get(key) {
    if (this.size) {
      if (key === this.lastItem.key) {
        if (this.options.updateExpire) {
          this.expireAfter(key);
        }

        return this.lastItem.value;
      }

      var index = findIndexAfterFirst(this.list, key);

      if (~index) {
        if (this.options.updateExpire) {
          this.expireAfter(key);
        }

        this.lastItem = this.list[index];

        return unshift(splice(this.list, index), this.lastItem).value;
      }
    }
  };

  /**
   * @private
   *
   * @function has
   *
   * @description
   * does the key exist in the cache
   *
   * @param {*} key the key to find in cache
   * @returns {boolean} does the key exist in cache
   */


  Cache.prototype.has = function has(key) {
    return this.size !== 0 && (key === this.lastItem.key || !!~findIndexAfterFirst(this.list, key));
  };

  /**=
   * @function remove
   * @memberof Cache
   * @instance
   *
   * @description
   * remove the item at key from cach
   *
   * @param {*} key the key to remove from cache
   * @returns {void}
   */


  Cache.prototype.remove = function remove(key) {
    var index = findIndex(this.list, key);

    if (~index) {
      splice(this.list, index);

      if (this.size === 1) {
        return this.clear();
      }

      this.size--;

      if (!index) {
        this.lastItem = this.list[0];
      }
    }
  };

  /**
   * @function update
   * @memberof Cache
   * @instance
   *
   * @description
   * update an item in-place with a new value
   *
   * @param {*} key key to update value of
   * @param {*} value value to store in the map at key
   */


  Cache.prototype.update = function update(key, value) {
    var index = findIndex(this.list, key);

    if (~index) {
      this.list[index].value = value;

      if (this.lastItem && key === this.lastItem.key) {
        this.lastItem.value = value;
      }
    }
  };

  return Cache;
}();

export default Cache;


//////////////////
// WEBPACK FOOTER
// ./~/moize/es/Cache.js
// module id = 329
// module chunks = 0